import { ProbeResult } from "../types";

export interface SecurityCheck {
  id: string;
  label: string;
  description: string;
  points: number;
  evaluate: (headers: Record<string, string | null>) => boolean;
}

export const SECURITY_CHECKS: SecurityCheck[] = [
  {
    id: "hsts",
    label: "HSTS",
    description: "Strict-Transport-Security with max-age ≥ 1 year",
    points: 12,
    evaluate: (h) => {
      const val = h["strict-transport-security"];
      if (!val) return false;
      const m = val.match(/max-age=(\d+)/i);
      return !!m && parseInt(m[1], 10) >= 31536000;
    },
  },
  {
    id: "csp",
    label: "Content Security Policy",
    description: "Content-Security-Policy header limits resource loading",
    points: 10,
    evaluate: (h) => !!h["content-security-policy"],
  },
  {
    id: "xcto",
    label: "MIME Sniffing Protection",
    description: "X-Content-Type-Options: nosniff prevents MIME confusion attacks",
    points: 7,
    evaluate: (h) =>
      h["x-content-type-options"]?.toLowerCase().includes("nosniff") ?? false,
  },
  {
    id: "xfo",
    label: "Clickjacking Protection",
    description: "X-Frame-Options prevents the page from being embedded in iframes",
    points: 7,
    evaluate: (h) => !!h["x-frame-options"],
  },
  {
    id: "no_info_leak",
    label: "No Server Info Disclosure",
    description: "Server and X-Powered-By headers are absent",
    points: 7,
    evaluate: (h) => !h["server"] && !h["x-powered-by"],
  },
  {
    id: "referrer",
    label: "Referrer Policy",
    description: "Referrer-Policy controls what URL info is shared with other sites",
    points: 5,
    evaluate: (h) => !!h["referrer-policy"],
  },
  {
    id: "permissions",
    label: "Permissions Policy",
    description: "Permissions-Policy restricts access to browser features",
    points: 5,
    evaluate: (h) => !!h["permissions-policy"],
  },
  {
    id: "compression",
    label: "Response Compression",
    description: "Content-Encoding present — gzip/brotli reduces transfer size",
    points: 4,
    evaluate: (h) => !!h["content-encoding"],
  },
  {
    id: "cors",
    label: "CORS Not Wildcard",
    description: "Access-Control-Allow-Origin is not open to all origins (*)",
    points: 3,
    evaluate: (h) => {
      const val = h["access-control-allow-origin"];
      return !val || val.trim() !== "*";
    },
  },
];

export function speedPoints(ttfbMs: number | null): number {
  if (ttfbMs === null) return 0;
  if (ttfbMs <= 150) return 5;
  if (ttfbMs <= 300) return 4;
  if (ttfbMs <= 600) return 3;
  if (ttfbMs <= 1000) return 1;
  return 0;
}

function scoreColor(pct: number): string {
  if (pct >= 0.8) return "#39D98A";
  if (pct >= 0.6) return "#FDDD48";
  if (pct >= 0.4) return "#FDDD48";
  return "#FF5C5C";
}

interface ScorePanelProps {
  results: ProbeResult[];
  selectedRegions: string[];
  enabledChecks: Set<string>;
  onToggleCheck: (id: string) => void;
}

export default function ScorePanel({ results, selectedRegions, enabledChecks }: ScorePanelProps) {

  // Use headers from the first result that has them (endpoint headers are globally consistent).
  const headersResult = results.find((r) => r.response_headers != null);
  const headers = headersResult?.response_headers ?? null;

  // Evaluate each security check.
  const checkResults: Record<string, boolean | null> = {};
  for (const check of SECURITY_CHECKS) {
    checkResults[check.id] = headers ? check.evaluate(headers) : null;
  }

  // Security score.
  let secScore = 0;
  let secMax = 0;
  for (const check of SECURITY_CHECKS) {
    if (!enabledChecks.has(check.id)) continue;
    secMax += check.points;
    if (checkResults[check.id] === true) secScore += check.points;
  }

  // Speed score — 5 pts per selected region, scaled by TTFB.
  let speedScore = 0;
  const speedMax = selectedRegions.length * 5;
  for (const region of selectedRegions) {
    const result = results.find((r) => r.region === region);
    speedScore += speedPoints(result?.ttfb_ms ?? null);
  }

  const total = secScore + speedScore;
  const max = secMax + speedMax;
  const pct = max > 0 ? total / max : 0;
  const color = scoreColor(pct);

  return (
    <div
      className="glass"
      style={{ padding: 24, marginTop: 12 }}
    >
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 36, fontWeight: 800, color, lineHeight: 1 }}>{total}</span>
        <span style={{ fontSize: 18, color: "#98A1AB", fontWeight: 600 }}>
          / {max} pts
        </span>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#98A1AB", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
          Score
        </span>
      </div>

      {/* Progress bar */}
      <div
        style={{
          height: 6,
          background: "rgba(255,255,255,0.08)",
          borderRadius: 3,
          overflow: "hidden",
          marginBottom: 14,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${Math.round(pct * 100)}%`,
            background: color,
            borderRadius: 3,
            transition: "width 0.5s ease",
          }}
        />
      </div>

      {/* Sub-score pills */}
      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 16,
          fontSize: 13,
          color: "#98A1AB",
        }}
      >
        <span>
          Security{" "}
          <strong style={{ color: "#EFF2F5" }}>
            {secScore}/{secMax}
          </strong>
        </span>
        <span style={{ opacity: 0.3 }}>·</span>
        <span>
          Speed{" "}
          <strong style={{ color: "#EFF2F5" }}>
            {speedScore}/{speedMax}
          </strong>
        </span>
      </div>

      {/* Security check results */}
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {SECURITY_CHECKS.map((check) => {
          const enabled = enabledChecks.has(check.id);
          const pass = checkResults[check.id];

          return (
            <div
              key={check.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "8px 12px",
                borderRadius: 3,
                background: "rgba(18,18,18,0.4)",
                opacity: enabled ? 1 : 0.4,
                transition: "opacity 0.15s",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#EFF2F5" }}>
                  {check.label}
                </div>
                <div style={{ fontSize: 11, color: "#98A1AB", marginTop: 1 }}>
                  {check.description}
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                {!enabled ? (
                  <span style={{ fontSize: 11, color: "#98A1AB" }}>disabled</span>
                ) : pass === null ? (
                  <span style={{ fontSize: 13, color: "#98A1AB" }}>—</span>
                ) : pass ? (
                  <span style={{ color: "#39D98A", fontSize: 15, lineHeight: 1 }}>✓</span>
                ) : (
                  <span style={{ color: "#FF5C5C", fontSize: 15, lineHeight: 1 }}>✗</span>
                )}
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    minWidth: 44,
                    textAlign: "right",
                    color: !enabled
                      ? "#98A1AB"
                      : pass
                      ? "#39D98A"
                      : "#98A1AB",
                  }}
                >
                  {enabled ? (pass ? `+${check.points}` : `+0`) : `(${check.points})`}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
